import { useNotificationState } from '@xstudios/xapp-core/foundation'
import { defineStore } from 'pinia'

import { getJob } from '@/domains/jobs/services/jobService'

const POLL_INTERVAL_MS = 2000
const TERMINAL_STATUSES = new Set(['cancelled', 'completed', 'failed'])

export const useJobNotificationsStore = defineStore('jobNotifications', {
  actions: {
    async pollPendingJobs() {
      if (this.pollInFlight || this.pendingJobs.length === 0) {
        this.stopPollingIfIdle()
        return
      }
      this.pollInFlight = true

      const { notify } = useNotificationState()
      const stillPending = []

      try {
        for (const entry of this.pendingJobs) {
          const jobId = String(entry.jobId || '').trim()
          if (!jobId) {
            continue
          }
          try {
            const snapshot = await getJob(jobId)
            const status = String(snapshot?.status || '')
              .trim()
              .toLowerCase()
            if (!TERMINAL_STATUSES.has(status)) {
              stillPending.push(entry)
              continue
            }

            if (status === 'completed') {
              notify({
                color: 'success',
                text: entry.successMessage,
              })
              continue
            }

            const detail = String(
              snapshot?.error?.detail || snapshot?.error?.message || ''
            ).trim()
            notify({
              color: 'error',
              text: detail
                ? `${entry.failureMessage} ${detail}`
                : entry.failureMessage,
            })
          } catch (error) {
            const statusCode = Number(error?.response?.status || 0)
            // Job lookup can briefly race right after enqueue; keep tracking.
            if (statusCode === 404) {
              stillPending.push(entry)
              continue
            }
            notify({
              color: 'error',
              text: entry.failureMessage,
            })
          }
        }
      } finally {
        this.pendingJobs = stillPending
        this.pollInFlight = false
        this.stopPollingIfIdle()
      }
    },

    resumePolling() {
      if (this.pollTimer || this.pendingJobs.length === 0) {
        return
      }
      this.pollTimer = setInterval(() => {
        this.pollPendingJobs().catch((error) => {
          console.error('Failed polling background jobs:', error)
        })
      }, POLL_INTERVAL_MS)
      this.pollPendingJobs().catch((error) => {
        console.error('Failed polling background jobs:', error)
      })
    },

    stopPollingIfIdle() {
      if (this.pendingJobs.length > 0 || !this.pollTimer) {
        return
      }
      clearInterval(this.pollTimer)
      this.pollTimer = null
    },

    trackJob({
      failureMessage = 'Background job failed.',
      jobId = '',
      successMessage = 'Background job completed successfully.',
    } = {}) {
      const safeJobId = String(jobId || '').trim()
      if (!safeJobId) {
        return
      }
      if (
        this.pendingJobs.some((job) => String(job.jobId || '') === safeJobId)
      ) {
        return
      }
      this.pendingJobs.push({
        failureMessage:
          String(failureMessage || '').trim() || 'Background job failed.',
        jobId: safeJobId,
        successMessage:
          String(successMessage || '').trim() ||
          'Background job completed successfully.',
      })
      this.resumePolling()
    },
  },
  persist: {
    key: 'jobs-notifications',
    paths: ['pendingJobs'],
  },
  state: () => ({
    pendingJobs: [],
    pollInFlight: false,
    pollTimer: null,
  }),
})
